import React, { useState, useEffect } from 'react';
import Dialog from '../common/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCostCodesData } from '../../store/slices/proposalsSlice';
import { PencilIcon, ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { showToast } from '../utils/Toast';

const AddCostCodeDialog = ({ isOpen, onClose, onSave, proposalId }) => {
  const dispatch = useDispatch();
  const costCodesData = useSelector(state => state.proposals.costCodesData);
  const loading = useSelector(state => state.proposals.loading);
  const [isEditing, setIsEditing] = useState(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    item: '',
    description: '',
    quantity: 1,
    unit: 'EA',
    unitCost: 0,
    builderCost: 0,
    markup: 49.25,
    clientPrice: 0,
    margin: 0,
    costType: [],
    markedAs: -1
  });

  // Reset form data and state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        item: '',
        description: '',
        quantity: 1,
        unit: 'EA',
        unitCost: 0,
        builderCost: 0,
        markup: 49.25,
        clientPrice: 0,
        margin: 0,
        costType: [],
        markedAs: -1
      });
      setIsEditing(false);
      setShowAdvancedFields(false);
      setSearchTerm('');
      setSelectedItems([]);
    }
  }, [isOpen]);

  const labelClasses = "block text-xs uppercase text-gray-500 mb-1";
  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;
  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white resize-none h-24
  `;

  const calculateBuilderCost = (quantity, unitCost) => {
    return quantity * unitCost;
  };

  const calculateMargin = (builderCost, clientPrice) => {
    if (builderCost && clientPrice) {
      return ((clientPrice - builderCost) / builderCost * 100).toFixed(2);
    }
    return 0;
  };

  const handleChange = (field, value) => {
    const updates = { [field]: value };
    
    if (field === 'quantity' || field === 'unitCost') {
      const quantity = field === 'quantity' ? Number(value) : Number(formData.quantity);
      const unitCost = field === 'unitCost' ? Number(value) : Number(formData.unitCost);
      updates.builderCost = calculateBuilderCost(quantity, unitCost);
      updates.margin = calculateMargin(updates.builderCost, formData.clientPrice);
    }

    if (field === 'clientPrice') {
      updates.margin = calculateMargin(formData.builderCost, Number(value));
    }

    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleSubmit = async () => {
    // Check if single item mode or multiple items mode
    if (selectedItems.length === 0 && formData.item) {
      // Single item mode - ensure required fields are present
      if (!formData.item) {
        showToast('Item name is required', 'error');
        return;
      }

      // Create the cost item data with proper number conversions
      const costItemData = {
        item: formData.item.trim(),
        description: formData.description.trim(),
        quantity: Number(formData.quantity) || 1,
        unit: formData.unit !== undefined && formData.unit !== '' ? formData.unit : 'EA',
        unitCost: Number(formData.unitCost) || 0,
        builderCost: Number(formData.builderCost) || 0,
        markup: Number(formData.markup) || 49.25,
        clientPrice: Number(formData.clientPrice) || 0,
        margin: Number(formData.margin) || 0,
        proposal: proposalId,
        costType: formData.costType || [],
        markedAs: formData.markedAs || -1
      };

      // Call the onSave function with the prepared data
      onSave(costItemData);
    } else if (selectedItems.length > 0) {
      // Multiple items mode
      try {
        // Create a base cost item with the current form data (without the item name)
        const baseCostItem = {
          description: formData.description.trim(),
          quantity: Number(formData.quantity) || 1,
          unit: formData.unit !== undefined && formData.unit !== '' ? formData.unit : 'EA',
          unitCost: Number(formData.unitCost) || 0,
          builderCost: Number(formData.builderCost) || 0,
          markup: Number(formData.markup) || 49.25,
          clientPrice: Number(formData.clientPrice) || 0,
          margin: Number(formData.margin) || 0,
          proposal: proposalId,
          costType: formData.costType || [],
          markedAs: formData.markedAs || -1
        };

        // Process each selected item
        for (const itemId of selectedItems) {
          const selectedCode = costCodesData.find(code => code._id === itemId);
          if (selectedCode) {
            const itemData = {
              ...baseCostItem,
              item: selectedCode.item,
              // Optionally override with item-specific values if needed
              description: selectedCode.description || baseCostItem.description,
              quantity: selectedCode.quantity || baseCostItem.quantity,
              unit: selectedCode.unit !== undefined && selectedCode.unit !== '' ? selectedCode.unit : 'EA',
              unitCost: selectedCode.unitCost || baseCostItem.unitCost,
              builderCost: calculateBuilderCost(
                selectedCode.quantity || baseCostItem.quantity, 
                selectedCode.unitCost || baseCostItem.unitCost
              ),
              markup: selectedCode.markup || baseCostItem.markup,
              clientPrice: selectedCode.clientPrice || baseCostItem.clientPrice,
            };
            
            // Calculate margin based on builder cost and client price
            itemData.margin = calculateMargin(itemData.builderCost, itemData.clientPrice);
            
            // Save each item individually
            await onSave(itemData);
          }
        }
        
        showToast(`Added ${selectedItems.length} items successfully`, 'success');
      } catch (error) {
        showToast('Error adding multiple items', 'error');
        console.error('Error adding multiple items:', error);
      }
    } else {
      showToast('No items selected', 'error');
      return;
    }
    
    // Reset form data and selected items
    setFormData({
      item: '',
      description: '',
      quantity: 1,
      unit: 'EA',
      unitCost: 0,
      builderCost: 0,
      markup: 49.25,
      clientPrice: 0,
      margin: 0,
      costType: [],
      markedAs: -1
    });
    setSelectedItems([]);
    setSearchTerm('');
    
    onClose();
  };

  const handleCostCodeSelect = (item) => {
    const selectedCode = costCodesData.find(code => code.item === item);
    if (selectedCode) {
      const builderCost = calculateBuilderCost(
        selectedCode.quantity || 1,
        selectedCode.unitCost || 0
      );

      const margin = calculateMargin(
        builderCost,
        selectedCode.clientPrice || 0
      );

      setFormData(prev => ({
        ...prev,
        item: selectedCode.item,
        description: selectedCode.description || '',
        quantity: selectedCode.quantity || 1,
        unit: selectedCode.unit !== undefined && selectedCode.unit !== '' ? selectedCode.unit : 'EA',
        unitCost: selectedCode.unitCost || 0,
        builderCost: builderCost,
        markup: selectedCode.markup || 49.25,
        clientPrice: selectedCode.clientPrice || 0,
        margin: margin,
        costType: selectedCode.costType || [],
        markedAs: selectedCode.markedAs || -1
      }));
    }
  };

  const handleToggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Filter cost codes based on search term
  const filteredCostCodes = costCodesData.filter(code => 
    code.item?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCostCodesData());
    }
  }, [dispatch, isOpen]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Add New Cost Code</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Search bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className={`${inputClasses} pl-10`}
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Item selection - either single select or multi-select */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className={labelClasses}>Items</label>
              <label className="flex items-center text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={selectedItems.length > 0}
                  onChange={() => setSelectedItems(selectedItems.length > 0 ? [] : [])}
                  className="mr-2"
                />
                Multiple Selection
              </label>
            </div>

            {/* Item list with checkboxes */}
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading cost codes...</div>
              ) : filteredCostCodes.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No items found</div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {filteredCostCodes.map(code => (
                    <li key={code._id} className="flex items-center p-3 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        id={`item-${code._id}`}
                        checked={selectedItems.includes(code._id)}
                        onChange={() => handleToggleItemSelection(code._id)}
                        className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label 
                        htmlFor={`item-${code._id}`}
                        className="flex-1 flex flex-col cursor-pointer"
                      >
                        <span className="font-medium text-gray-900">{code.item}</span>
                        {code.description && (
                          <span className="text-sm text-gray-500 truncate">{code.description}</span>
                        )}
                      </label>
                      <span className="text-gray-500">${code.unitCost || 0}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Button for showing detailed fields - hidden for now */}
          {/* 
          <button
            onClick={() => setShowAdvancedFields(!showAdvancedFields)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            {showAdvancedFields ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
            <span>{showAdvancedFields ? 'Hide detailed fields' : 'Show detailed fields'}</span>
          </button>
          */}

          {showAdvancedFields && (
            <div className="space-y-4">
              <div>
                <label className={labelClasses}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className={textAreaClasses}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClasses}>Quantity</label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label className={labelClasses}>Unit</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => handleChange('unit', e.target.value)}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label className={labelClasses}>Unit Cost</label>
                  <input
                    type="number"
                    value={formData.unitCost}
                    onChange={(e) => handleChange('unitCost', e.target.value)}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label className={labelClasses}>Builder Cost</label>
                  <input
                    type="number"
                    value={formData.builderCost}
                    className={`${inputClasses} bg-gray-50`}
                    readOnly
                  />
                </div>

                <div>
                  <label className={labelClasses}>Markup %</label>
                  <input
                    type="number"
                    value={formData.markup}
                    onChange={(e) => handleChange('markup', e.target.value)}
                    className={inputClasses}
                    step="any"
                  />
                </div>

                <div>
                  <label className={labelClasses}>Client Price</label>
                  <input
                    type="number"
                    value={formData.clientPrice}
                    onChange={(e) => handleChange('clientPrice', e.target.value)}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label className={labelClasses}>Margin %</label>
                  <input
                    type="number"
                    value={formData.margin}
                    className={`${inputClasses} bg-gray-50`}
                    readOnly
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {selectedItems.length > 0 ? `Save ${selectedItems.length} Items` : 'Save Cost Code'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AddCostCodeDialog; 